import { createFileRoute } from "@tanstack/react-router";
import { WEB_API_BASE_PATH } from "common/constants";
import Flags from "common/constants/flags";
import objectIsEmpty from "common/utils/objectIsEmpty";
import Toggle from "lib/components/actions/Toggle";
import { FetchErrorMessage } from "lib/components/errors/FetchErrorMessage";
import { fetcher, useFetch } from "lib/fetcher";
import getPageTitle from "lib/utils/getPageTitle";
import { useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import Button from "ui/buttons/Button";
import CodeBlock from "ui/components/CodeBlock";
import ConfirmModal from "ui/components/ConfirmModal";
import withAuthentication from "../../components/withAuthentication";
import styles from "./server-errors.module.scss";

const errorCodes = [400, 401, 403, 404, 500];
type ErrorCodes = (typeof errorCodes)[number];

function errorEndpoint(code?: ErrorCodes, simulation?: "mem:leak" | "mem:spike" | "mem:clear", sizeMB?: number) {
  const params = new URLSearchParams();
  if (code) {
    params.set("code", String(code));
  }
  if (simulation) {
    params.set("simulation", simulation);
  }
  if (sizeMB) {
    params.set("sizeMB", String(sizeMB));
  }
  return `${WEB_API_BASE_PATH}/debug/server-error?${params.toString()}`;
}

const datadogLambdaErrorTestEndpoint = `${WEB_API_BASE_PATH}/debug/datadog-lambda-error-test`;

function ErrorsPage() {
  const isProduction = Flags.DB_ENV === "prod";
  const [memoryResponse, setMemoryResponse] = useState({});
  const [fetcherResponse, setFetcherResponse] = useState({});
  const [handleErrorLocally, setHandleErrorLocally] = useState(true);
  const [useFetchCode, setUseFetchCode] = useState<ErrorCodes | null>(null);
  const [showSpike4GBConfirm, setShowSpike4GBConfirm] = useState(false);
  const [show1000RequestsConfirm, setShow1000RequestsConfirm] = useState(false);
  const [datadogLambdaTestResponse, setDatadogLambdaTestResponse] = useState({});

  const { error: useFetchError } = useFetch(errorEndpoint(useFetchCode ?? 0), [useFetchCode], {
    handleErrorLocally,
    shouldRetryOnError: false,
  });

  const submitMemorySimulation = async (simulation?: "mem:leak" | "mem:spike" | "mem:clear", sizeMB?: number) => {
    try {
      const resp = await fetcher(errorEndpoint(undefined, simulation, sizeMB));
      setMemoryResponse(resp ?? {});
    } catch (e) {
      setMemoryResponse({ error: e instanceof Error ? e.message : String(e) });
    }
  };

  const submitConcurrentRequests = async (totalRequests: number, spikeCount: number, spikeSizeMB: number = 200) => {
    const requests: Promise<unknown>[] = [];

    for (let i = 0; i < totalRequests; i++) {
      const isSpike = i < spikeCount;
      const endpoint = isSpike ? errorEndpoint(undefined, "mem:spike", spikeSizeMB) : errorEndpoint(); // status check
      requests.push(fetcher(endpoint).catch((e) => e));
    }

    const results = await Promise.all(requests);
    setMemoryResponse({
      concurrentTest: {
        totalRequests,
        spikeCount,
        spikeSizeMB,
        results,
      },
    });
  };

  const submitFetcherError = async (code: ErrorCodes) => {
    try {
      const resp = await fetcher(errorEndpoint(code));
      setFetcherResponse(resp ?? {});
    } catch (e) {
      setFetcherResponse(e);
    }
  };

  const invokeDatadogLambdaFailureTest = async () => {
    try {
      const resp = await fetcher(datadogLambdaErrorTestEndpoint, true, {}, {});
      setDatadogLambdaTestResponse(resp ?? {});
    } catch (e) {
      setDatadogLambdaTestResponse({ error: e instanceof Error ? e.message : String(e) });
    }
  };

  return (
    <>
      <Head>
        <title>{getPageTitle("Server Errors")}</title>
      </Head>
      <div className={styles.wrapper}>
        <h1>Server Errors</h1>
        <div className={styles.columns}>
          {!isProduction && (
            <>
              <section>
                <h2>Task Lambda — Datadog error tags</h2>
                <p>
                  POSTs to the API to invoke Task Lambda with <code>DatadogErrorTagTest</code> (handler throws). On
                  deployed AWS you should see a failure payload here and error tags on the Lambda span in Datadog. Local
                  dev uses the in-process shim and typically reports <code>invoke_threw</code> instead.
                </p>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <Button btnSize="medium" btnStyle="remove" onClick={() => void invokeDatadogLambdaFailureTest()}>
                    Invoke failing Task Lambda
                  </Button>
                </div>
                {!objectIsEmpty(datadogLambdaTestResponse) && (
                  <CodeBlock style={{ maxHeight: "250px" }} code={JSON.stringify(datadogLambdaTestResponse, null, 2)} />
                )}
              </section>
              <section>
                <h2>Simulate memory problems</h2>
                <p>
                  Pair with <code>yarn dev:low-memory</code> to lower thresholds.
                  <br />
                  Spike threshold = 65% of TASK_MEMORY_MB, Leak threshold = 20MB delta per request.
                  <br />
                  See <code>docs/SERVER_LOGGING.md</code> for details.
                </p>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <Button btnSize="medium" onClick={() => submitMemorySimulation("mem:leak", 50)}>
                    Leak +50MB
                  </Button>
                  <Button btnSize="medium" onClick={() => submitMemorySimulation("mem:leak", 100)}>
                    Leak +100MB
                  </Button>
                  <Button btnSize="medium" onClick={() => submitMemorySimulation("mem:spike", 200)}>
                    Spike 200MB
                  </Button>
                  <Button btnSize="medium" onClick={() => submitMemorySimulation("mem:spike", 500)}>
                    Spike 500MB
                  </Button>
                  <Button btnSize="medium" onClick={() => submitMemorySimulation("mem:spike", 1024)}>
                    Spike 1GB
                  </Button>
                  <Button btnSize="medium" btnStyle="remove" onClick={() => setShowSpike4GBConfirm(true)}>
                    Spike 4GB
                  </Button>
                  <Button btnSize="medium" onClick={() => submitMemorySimulation("mem:clear")}>
                    Clear retained
                  </Button>
                  <Button btnSize="medium" onClick={() => submitMemorySimulation()}>
                    Check status
                  </Button>
                </div>
                <h3 style={{ marginTop: "1rem" }}>Concurrent requests</h3>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <Button btnSize="medium" onClick={() => submitConcurrentRequests(5, 1)}>
                    5 reqs (1 spike)
                  </Button>
                  <Button btnSize="medium" onClick={() => submitConcurrentRequests(10, 2)}>
                    10 reqs (2 spikes)
                  </Button>
                  <Button btnSize="medium" onClick={() => submitConcurrentRequests(10, 5)}>
                    10 reqs (5 spikes)
                  </Button>
                  <Button btnSize="medium" onClick={() => submitConcurrentRequests(20, 3)}>
                    20 reqs (3 spikes)
                  </Button>
                  <Button btnSize="medium" onClick={() => submitConcurrentRequests(100, 10)}>
                    100 reqs (10 spikes)
                  </Button>
                  <Button btnSize="medium" btnStyle="remove" onClick={() => setShow1000RequestsConfirm(true)}>
                    1000 reqs (50 spikes)
                  </Button>
                </div>
                {!objectIsEmpty(memoryResponse) && (
                  <CodeBlock style={{ maxHeight: "250px" }} code={JSON.stringify(memoryResponse, null, 2)} />
                )}
              </section>
            </>
          )}
          <div>
            <section>
              <h2>useFetch()</h2>
              <div style={{ display: "flex", gap: "1rem" }}>
                <label htmlFor="handleErrorLocally">handleErrorLocally</label>
                <Toggle
                  variants="small"
                  isTrue={handleErrorLocally}
                  onChange={(val) => setHandleErrorLocally(val)}
                  id="handleErrorLocally"
                />
              </div>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {errorCodes.map((code) => (
                  <Button
                    btnSize="medium"
                    btnStyle={code === 500 ? "remove" : undefined}
                    key={`error-button-${code}`}
                    onClick={() => setUseFetchCode(code)}
                  >
                    Test {code}
                  </Button>
                ))}
              </div>
              {useFetchError && (
                <div>
                  <b>FetchErrorMessage</b>
                  <FetchErrorMessage fetchError={useFetchError ?? null} />
                  <b>Response</b>
                  <CodeBlock style={{ maxHeight: "50px" }} code={JSON.stringify(useFetchError, null, 2)} />
                </div>
              )}
            </section>
            <section style={{ marginTop: "1rem" }}>
              <h2>fetcher()</h2>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {errorCodes.map((code) => (
                  <Button
                    btnSize="medium"
                    btnStyle={code === 500 ? "remove" : undefined}
                    key={`error-button-${code}`}
                    onClick={() => submitFetcherError(code)}
                  >
                    Test {code}
                  </Button>
                ))}
              </div>
              {!objectIsEmpty(fetcherResponse) && (
                <CodeBlock style={{ maxHeight: "50px" }} code={JSON.stringify(fetcherResponse, null, 2)} />
              )}
            </section>
          </div>
        </div>
      </div>
      <ConfirmModal
        open={showSpike4GBConfirm}
        onClose={() => setShowSpike4GBConfirm(false)}
        onConfirm={async () => {
          setShowSpike4GBConfirm(false);
          await submitMemorySimulation("mem:spike", 4096);
        }}
        title="Confirm 4GB Memory Spike"
        message="This will allocate 4GB of memory, which could kill the server task. Are you sure you want to proceed?"
      />
      <ConfirmModal
        open={show1000RequestsConfirm}
        onClose={() => setShow1000RequestsConfirm(false)}
        onConfirm={async () => {
          setShow1000RequestsConfirm(false);
          await submitConcurrentRequests(1000, 50);
        }}
        title="Confirm 1000 Concurrent Requests"
        message="This will send 1000 concurrent requests to the server, which could overload it. Are you sure you want to proceed?"
      />
    </>
  );
}

export const Route = createFileRoute("/debug/server-errors")({
  component: withAuthentication(ErrorsPage, { superAdmin: true }),
});
